import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { Flashcard, SectionFilter } from '@/types';

interface FlashcardsState {
  flashcards: Flashcard[];
  dueToday: Flashcard[];
  currentIndex: number;
  isLoading: boolean;
  error: string | null;
  setFlashcards: (flashcards: Flashcard[]) => void;
  setDueToday: (flashcards: Flashcard[]) => void;
  nextCard: () => void;
  previousCard: () => void;
  setCurrentIndex: (index: number) => void;
  updateFlashcard: (id: string, updates: Partial<Flashcard>) => void;
  setLoading: (loading: boolean) => void;
  fetchFlashcards: (userId: string) => Promise<void>;
  fetchDueFlashcards: (userId: string) => Promise<void>;
  reviewCard: (id: string, quality: 0 | 1 | 2 | 3) => Promise<void>;
  generateFlashcards: (documentIds: string[], userId: string, count: number, sectionFilters?: SectionFilter[], difficulty?: 'easy' | 'medium' | 'hard') => Promise<string | undefined>;
}

export const useFlashcardsStore = create<FlashcardsState>((set, get) => ({
  flashcards: [],
  dueToday: [],
  currentIndex: 0,
  isLoading: false,
  error: null,
  setFlashcards: (flashcards) => set({ flashcards }),
  setDueToday: (dueToday) => set({ dueToday }),
  nextCard: () =>
    set((state) => ({
      currentIndex: Math.min(state.currentIndex + 1, state.dueToday.length - 1),
    })),
  previousCard: () =>
    set((state) => ({
      currentIndex: Math.max(state.currentIndex - 1, 0),
    })),
  setCurrentIndex: (currentIndex) => set({ currentIndex }),
  updateFlashcard: (id, updates) =>
    set((state) => ({
      flashcards: state.flashcards.map((card) =>
        card.id === id ? { ...card, ...updates } : card
      ),
      dueToday: state.dueToday.map((card) =>
        card.id === id ? { ...card, ...updates } : card
      ),
    })),
  setLoading: (isLoading) => set({ isLoading }),

  fetchFlashcards: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchFlashcards');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ flashcards: data || [], isLoading: false });
  },

  fetchDueFlashcards: async (userId: string) => {
    set({ isLoading: true, error: null });
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format for date column

    const { data, error } = await supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', userId)
      .lte('due_date', today)
      .order('due_date', { ascending: true });

    if (error) {
      reportSupabaseError(error, 'fetchDueFlashcards');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ dueToday: data || [], currentIndex: 0, isLoading: false });
  },

  reviewCard: async (id: string, quality: 0 | 1 | 2 | 3) => {
    Sentry.addBreadcrumb({ category: 'flashcard', message: `Card reviewed: quality=${quality}`, level: 'info' });
    const card = get().flashcards.find((c) => c.id === id) || get().dueToday.find((c) => c.id === id);
    if (!card) return;

    // SM-2 algorithm
    let { ease_factor, interval_days, review_count } = card;

    if (quality < 2) {
      // Failed - reset
      interval_days = 1;
    } else {
      // Passed
      if (review_count === 0) {
        interval_days = 1;
      } else if (review_count === 1) {
        interval_days = 6;
      } else {
        interval_days = Math.round(interval_days * ease_factor);
      }
    }

    // Update ease factor
    ease_factor = Math.max(1.3, ease_factor + (0.1 - (3 - quality) * (0.08 + (3 - quality) * 0.02)));

    const due_date = new Date();
    due_date.setDate(due_date.getDate() + interval_days);

    const updates = {
      ease_factor,
      interval_days,
      review_count: review_count + 1,
      due_date: due_date.toISOString().split('T')[0], // YYYY-MM-DD format
    };

    const { error: reviewError } = await supabase.from('flashcards').update(updates).eq('id', id);
    if (reviewError) {
      reportSupabaseError(reviewError, 'flashcardsStore.reviewCard');
    }

    // Remove from due today
    set((state) => ({
      dueToday: state.dueToday.filter((c) => c.id !== id),
    }));

    get().updateFlashcard(id, updates);
  },

  generateFlashcards: async (documentIds: string[], userId: string, count: number, sectionFilters?: SectionFilter[], difficulty?: 'easy' | 'medium' | 'hard') => {
    Sentry.addBreadcrumb({ category: 'flashcard', message: `Generation started: count=${count}, difficulty=${difficulty || 'medium'}`, level: 'info' });
    return Sentry.startSpan(
      { name: 'flashcards.generate', op: 'ai.generation', attributes: { 'generation.count': count, 'documents.count': documentIds.length, 'generation.difficulty': difficulty || 'medium' } },
      async () => {
    set({ isLoading: true, error: null });

    // 1. Create flashcard_generations tracking row
    const { data: generation, error: insertError } = await supabase
      .from('flashcard_generations')
      .insert({
        user_id: userId,
        document_ids: documentIds,
        requested_count: count,
        status: 'generating',
      })
      .select()
      .single();

    if (insertError || !generation) {
      set({ error: insertError?.message || 'Failed to start flashcard generation', isLoading: false });
      return;
    }

    set({ isLoading: false });

    // 2. Fire-and-forget edge function
    supabase.functions.invoke('generate-flashcards', {
      body: { generationId: generation.id, documentIds, count, sectionFilters, difficulty },
    }).catch((err: unknown) => {
      Sentry.captureException(err, { tags: { context: 'flashcardsStore.generateFlashcards' } });
    });

    // Return the generation ID for polling
    return generation.id;
      });
  },
}));
